import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { notificationService } from '../../lib/notification-service';

export default function NotificationTester() {
  const { family, currentUser } = useFamilyContext();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    familyMemberId: '',
    title: 'Test Notification',
    body: 'This is a test notification from PaperSedge!',
    type: 'message',
    priority: 'normal'
  });

  const handleSendNotification = async () => {
    if (!formData.familyMemberId) {
      setResult({ error: 'Please select a family member' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const result = await notificationService.sendNotification(formData);
      
      setResult({ 
        success: result.message || 'Notification logged successfully!',
        info: 'Note: Push delivery requires backend service setup'
      });
    } catch (error) {
      setResult({ error: 'Network error: ' + error.message });
    } finally {
      setSending(false);
    }
  };

  // Only show for parents/admins
  if (!currentUser?.isParent) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        📱 Notification Testing
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Send to Family Member
          </label>
          <select
            value={formData.familyMemberId}
            onChange={(e) => setFormData(prev => ({ ...prev, familyMemberId: e.target.value }))}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">Select a family member...</option>
            {family?.parents?.map(member => (
              <option key={member.id} value={member.id}>
                {member.name} (Parent) {member.id === currentUser?.id && '(You)'}
              </option>
            ))}
            {family?.kids?.map(member => (
              <option key={member.id} value={member.id}>
                {member.name} (Kid) {member.id === currentUser?.id && '(You)'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Message
          </label>
          <textarea
            value={formData.body}
            onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="message">Message</option>
              <option value="allowance">Allowance</option>
              <option value="task">Task</option>
              <option value="calendar">Calendar</option>
              <option value="shopping">Shopping</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSendNotification}
          disabled={sending}
          className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? 'Sending...' : 'Send Test Notification'}
        </button>

        {result && (
          <div className={`p-3 rounded-lg ${
            result.error 
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          }`}>
            {result.error || result.success}
            {result.info && (
              <div className="text-xs mt-1 opacity-75">
                {result.info}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}